import { useMemo } from "react";
import { useDashboardData } from "../context/DashboardDataContext";
import { formatPct, PATTERNS, PATTERN_LABELS, DIMENSIONS, DIMENSION_LABELS } from "../dashboard/analytics";
import { Card } from "../components/ui/Card";
import { PageHeader } from "../components/ui/PageHeader";
import { HorizontalBars } from "../components/charts/HorizontalBars";
import { HeatmapPairs } from "../components/charts/HeatmapPairs";

export function PatternIntelligence() {
  const { analytics, submissions } = useDashboardData();

  const tableRows = useMemo(() => {
    const total = analytics.completed || 1;
    return PATTERNS.map((p) => {
      const n = analytics.patternFrequency[p] || 0;
      const [conv, tot] = analytics.conversionByPattern[p] || [0, 0];
      return {
        pattern: PATTERN_LABELS[p] || p,
        count: n,
        share: n / total,
        conv: tot ? conv / tot : 0,
      };
    }).sort((a, b) => b.count - a.count);
  }, [analytics]);

  const severityBars = useMemo(() => {
    const completed = submissions.filter((s) => s.completed);
    return DIMENSIONS.map((dim) => {
      let sum = 0;
      let n = 0;
      completed.forEach((row) => {
        const score = row.patternScores[dim] ?? row.patternScores[DIMENSION_LABELS[dim]];
        if (score != null) {
          sum += Number(score) || 0;
          n += 1;
        }
      });
      const avg = n ? sum / n : 0;
      return {
        label: DIMENSION_LABELS[dim],
        value: (avg / 9) * 100,
        display: `${avg.toFixed(1)}/9`,
      };
    });
  }, [submissions]);

  const freqBars = useMemo(() => {
    const max = Math.max(1, ...PATTERNS.map((p) => analytics.patternFrequency[p] || 0));
    return PATTERNS.map((p) => ({
      label: PATTERN_LABELS[p] || p,
      value: analytics.patternFrequency[p] || 0,
      display: `${(((analytics.patternFrequency[p] || 0) / max) * 100).toFixed(0)}%`,
    })).sort((a, b) => b.value - a.value);
  }, [analytics]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Pattern intelligence"
        title="Decision debt patterns"
        description="Frequency, severity, co-occurrence, and revenue-adjusted scores."
      />

      <div className="grid-2">
        <Card title="Top patterns" subtitle="Share of assessments where pattern is primary">
          <HorizontalBars items={freqBars} />
        </Card>
        <Card title="Severity distribution" subtitle="Count by highest pattern score band">
          <ul className="stat-list">
            {Object.entries(analytics.severityDist).map(([k, v]) => (
              <li key={k}>
                <span>{k}</span>
                <strong>{v}</strong>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card title="Pattern performance table" subtitle="Occurrence and downstream conversion proxy">
        <div className="table-wrap table-wrap--rounded">
          <table className="data-table">
            <thead>
              <tr>
                <th>Pattern</th>
                <th>Occurrences</th>
                <th>Share</th>
                <th>Conv. proxy</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.pattern}>
                  <td>{row.pattern}</td>
                  <td>{row.count}</td>
                  <td>{formatPct(row.share)}</td>
                  <td>{formatPct(row.conv)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid-2">
        <Card title="Co-occurrence matrix" subtitle="Dimensions scoring ≥5 together (pair counts)">
          <HeatmapPairs pairCounts={analytics.pairCounts} />
        </Card>
        <Card title="Cross-pattern narrative" subtitle="What to watch this period">
          <div className="insight-stack">
            <div className="insight-card">
              <span className="insight-card__tag">Top pair</span>
              <p>
                {analytics.topPairs[0]
                  ? `${analytics.topPairs[0][0].split(" + ").map(d => DIMENSION_LABELS[d] || d).join(" + ")} (${analytics.topPairs[0][1]} joint signals).`
                  : "No overlapping high scores yet."}
              </p>
            </div>
            <div className="insight-card">
              <span className="insight-card__tag">Revenue lens</span>
              <p>
                Use the revenue table on Cohorts to compare reactive share and dependency scores
                by ARR band.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Average score by dimension (blended revenue)" subtitle="Higher = more debt signal on 0–9 scale">
        <HorizontalBars items={severityBars} max={100} />
      </Card>
    </div>
  );
}
